import { useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { AddEditDeleteBar1 } from '../stable/AddEditDeleteBar1';
import {
  updateObjective,
  createHumanRelationship,
  deleteHumanRelationship,
  createHumanWithRelationship,
} from '../../store/map3PendingSlice';
import { ResultItem3 } from './ResultItem3';
import { useCurrentMap } from '../../hooks/useCurrentMap';
// import { selectUserRoleForMap } from '../../store/selectors';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '../../utils/getPermissionsForRole';
import { HumanCombobox } from './HumanCombobox';
import type { Human } from '@mm-shared/types/Human';
import type { HumanRelationship } from '@mm-shared/types/HumanRelationship';
import { generateRelationshipDocId } from '@mm-shared/types/HumanRelationship';

export function ObjectiveInfoTab() {
  const [searchParams] = useSearchParams();
  const objectiveShortId = searchParams.get('objectiveId') ?? undefined;
  const milestoneMapUuid = searchParams.get('milestoneMapUuid') ?? '';
  const dispatch = useDispatch<AppDispatch>();

  const [isEditingOwner, setIsEditingOwner] = useState(false);
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [infoText, setInfoText] = useState('');
  const [isEditingPlans, setIsEditingPlans] = useState(false);
  const [planInputs, setPlanInputs] = useState<Array<{ text: string; url: string }>>([
    { text: '', url: '' },
  ]);
  const [planErrors, setPlanErrors] = useState<Array<{ text?: string; url?: string }>>([]);

  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj) => obj.shortId === objectiveShortId),
  );

  const humans = useSelector((state: RootState) => state.map3Data.humans);
  const humanRelationships = useSelector((state: RootState) => state.map3Data.humanRelationships);

  // Derive the current owner from HumanRelationship with relationshipType 'owner'
  const ownerHuman = useMemo(() => {
    if (!objective) return null;
    const ownerRel = humanRelationships.find(
      (rel) => rel.targetType === 'objective' && rel.targetUuid === objective.uuid && rel.relationshipType === 'owner',
    );
    if (!ownerRel) return null;
    return humans.find((h) => h.uuid === ownerRel.humanUuid) ?? null;
  }, [objective, humanRelationships, humans]);

  // Check user's role for edit permissions
  const currentMap = useCurrentMap();
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);

  if (!objective) {
    return <div>Objective not found</div>;
  }

  /**
   * Creates an owner HumanRelationship linking the given human to this objective.
   * Removes any existing owner relationship first so the new one takes effect
   * immediately in the ownerHuman memo (which uses .find() and would otherwise
   * return the old relationship).
   * Also updates ownerName on the objective for display compatibility.
   */
  const saveOwnerRelationship = async (human: Human) => {
    // Remove existing owner relationship if present, so .find() returns the new one
    const existingOwnerRel = humanRelationships.find(
      (rel) =>
        rel.targetType === 'objective' &&
        rel.targetUuid === objective.uuid &&
        rel.relationshipType === 'owner',
    );
    if (existingOwnerRel) {
      await dispatch(deleteHumanRelationship(existingOwnerRel));
    }

    const relUuid = generateRelationshipDocId('owner', human.uuid, objective.uuid);
    const relationship: HumanRelationship = {
      uuid: relUuid,
      shortId: relUuid.slice(0, 10),
      humanUuid: human.uuid,
      targetType: 'objective',
      targetUuid: objective.uuid,
      relationshipType: 'owner',
      relationshipName: 'Owner',
      version: 1,
      createdDate: new Date().toISOString(),
    };

    await dispatch(createHumanRelationship(relationship));
    // Update ownerName for display compatibility
    await dispatch(
      updateObjective({
        uuid: objective.uuid,
        updates: { ownerName: human.name },
      }),
    );
    setIsEditingOwner(false);
  };

  const handleOwnerSelect = async (human: Human) => {
    await saveOwnerRelationship(human);
  };

  const handleOwnerCreateNew = async (name: string) => {
    // Remove existing owner relationship if present, so .find() returns the new one
    const existingOwnerRel = humanRelationships.find(
      (rel) =>
        rel.targetType === 'objective' &&
        rel.targetUuid === objective.uuid &&
        rel.relationshipType === 'owner',
    );
    if (existingOwnerRel) {
      await dispatch(deleteHumanRelationship(existingOwnerRel));
    }

    const humanUuid = crypto.randomUUID();
    const human: Human = {
      uuid: humanUuid,
      shortId: humanUuid.slice(0, 10),
      name,
      version: 1,
      createdDate: new Date().toISOString(),
    };

    const relUuid = generateRelationshipDocId('owner', humanUuid, objective.uuid);
    const relationship: HumanRelationship = {
      uuid: relUuid,
      shortId: relUuid.slice(0, 10),
      humanUuid,
      targetType: 'objective',
      targetUuid: objective.uuid,
      relationshipType: 'owner',
      relationshipName: 'Owner',
      version: 1,
      createdDate: new Date().toISOString(),
    };

    await dispatch(createHumanWithRelationship({ human, relationship }));
    // Update ownerName for display compatibility
    await dispatch(
      updateObjective({
        uuid: objective.uuid,
        updates: { ownerName: name },
      }),
    );
    setIsEditingOwner(false);
  };

  const handleInfoSave = async () => {
    await dispatch(
      updateObjective({
        uuid: objective.uuid,
        updates: { additionalInfo: infoText },
      }),
    );
    setIsEditingInfo(false);
  };

  const handlePlansSave = async () => {
    // Reset errors
    const newErrors = planInputs.map(() => ({ text: '', url: '' }));
    let hasErrors = false;

    // Validate all inputs
    planInputs.forEach((plan, index) => {
      // Skip validation if both fields are empty
      if (!plan.text.trim() && !plan.url.trim()) {
        return;
      }

      // Validate if at least one field has content
      if (!plan.text.trim()) {
        newErrors[index].text = 'Text is required';
        hasErrors = true;
      }
      if (!plan.url.trim()) {
        newErrors[index].url = 'URL is required';
        hasErrors = true;
      } else {
        try {
          new URL(plan.url);
        } catch {
          newErrors[index].url = 'Please enter a valid URL';
          hasErrors = true;
        }
      }
    });

    if (hasErrors) {
      setPlanErrors(newErrors);
      return;
    }

    // Filter out empty entries and save
    const validPlans = planInputs.filter((plan) => plan.text.trim() && plan.url.trim());
    await dispatch(
      updateObjective({
        uuid: objective.uuid,
        updates: { plans: validPlans },
      }),
    );
    setIsEditingPlans(false);
    setPlanErrors([]);
  };

  return (
    <div className="space-y-6 p-4 flex flex-col h-full">
      {/* Milestones Section */}
      <section
        tabIndex={0}
        className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md"
        aria-labelledby="milestones-heading"
      >
        <h2 id="milestones-heading" className="text-lg font-semibold mb-2">
          Milestones
        </h2>
        <div className="space-y-1">
          {objective.results
            .filter((result) => result.isKeyResult)
            .map((result) => (
              <div
                key={result.uuid}
                className="flex items-start space-x-2 relative rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 p-1"
              >
                <ResultItem3
                  resultUuid={result.uuid}
                  objectiveUuid={objective.uuid}
                  deadline={result.deadline!}
                  showAddButton={false}
                />
              </div>
            ))}
          {objective.results.filter((result) => result.isKeyResult).length === 0 && (
            <p className="text-gray-600">No milestones defined yet.</p>
          )}
        </div>
      </section>

      {/* Owner Section */}
      <section
        tabIndex={0}
        className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md"
        aria-labelledby="owner-heading"
      >
        <h2 id="owner-heading" className="text-lg font-semibold mb-2">
          Owner
        </h2>
        <div className="p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 relative">
          {isEditingOwner ? (
            <div className="flex items-center">
              <HumanCombobox
                humans={humans}
                selectedHuman={ownerHuman}
                onSelect={handleOwnerSelect}
                onCreateNew={handleOwnerCreateNew}
                onCancel={() => setIsEditingOwner(false)}
                aria-label="Search for owner"
                autoFocus
                placeholder="Search or create owner..."
              />
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <span>{ownerHuman?.name || objective.ownerName || 'No owner assigned'}</span>
              <AddEditDeleteBar1
                isVisible={true}
                showAdd={false}
                showEdit={canEdit}
                showDelete={false}
                showInfo={false}
                onEdit={() => {
                  setIsEditingOwner(true);
                }}
              />
            </div>
          )}
        </div>
      </section>

      {/* Plans & Tasks Section */}
      <section
        tabIndex={0}
        className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md"
        aria-labelledby="plans-heading"
      >
        <h2 id="plans-heading" className="text-lg font-semibold mb-2">
          Plans & Tasks
        </h2>
        <div className="p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 relative">
          {isEditingPlans ? (
            <div className="space-y-2 max-w-2xl">
              {planInputs.map((plan, index) => (
                <div key={index} className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 space-y-1">
                      <input
                        type="text"
                        placeholder="text"
                        value={plan.text}
                        onChange={(e) => {
                          const newPlans = planInputs.map((p, i) =>
                            i === index ? { ...p, text: e.target.value } : { ...p },
                          );
                          setPlanInputs(newPlans);
                          if (planErrors[index]?.text) {
                            const newErrors = [...planErrors];
                            newErrors[index] = { ...newErrors[index], text: '' };
                            setPlanErrors(newErrors);
                          }
                        }}
                        className={`w-full px-2 py-1 rounded border ${
                          planErrors[index]?.text ? 'border-red-500' : 'border-gray-300'
                        } dark:bg-zinc-700 dark:text-white`}
                        aria-label={`Plan text ${index + 1}`}
                        aria-describedby={
                          planErrors[index]?.text ? `plan-text-error-${index}` : undefined
                        }
                      />
                      {planErrors[index]?.text && (
                        <div id={`plan-text-error-${index}`} className="text-red-500 text-sm">
                          {planErrors[index].text}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <input
                        type="text"
                        placeholder="link"
                        value={plan.url}
                        onChange={(e) => {
                          const newPlans = planInputs.map((p, i) =>
                            i === index ? { ...p, url: e.target.value } : { ...p },
                          );
                          setPlanInputs(newPlans);
                          if (planErrors[index]?.url) {
                            const newErrors = [...planErrors];
                            newErrors[index] = { ...newErrors[index], url: '' };
                            setPlanErrors(newErrors);
                          }
                        }}
                        className={`w-full px-2 py-1 rounded border ${
                          planErrors[index]?.url ? 'border-red-500' : 'border-gray-300'
                        } dark:bg-zinc-700 dark:text-white`}
                        aria-label={`Plan URL ${index + 1}`}
                        aria-describedby={
                          planErrors[index]?.url ? `plan-url-error-${index}` : undefined
                        }
                      />
                      {planErrors[index]?.url && (
                        <div id={`plan-url-error-${index}`} className="text-red-500 text-sm">
                          {planErrors[index].url}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex justify-between items-center mt-2">
                <button
                  onClick={() => setPlanInputs([...planInputs, { text: '', url: '' }])}
                  className="text-blue-600 hover:text-blue-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                >
                  + Add another plan
                </button>
                <div className="space-x-2">
                  <button
                    onClick={handlePlansSave}
                    className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingPlans(false);
                      setPlanInputs(objective.plans || [{ text: '', url: '' }]);
                    }}
                    className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-between items-start">
              <div className="space-y-1 flex-grow">
                {objective.plans && objective.plans.length > 0 ? (
                  objective.plans.map((plan, index) => (
                    <div key={index}>
                      <Link
                        to={plan.url}
                        className="text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                      >
                        {plan.text}
                      </Link>
                    </div>
                  ))
                ) : (
                  <span className="text-gray-500">No plans or tasks defined</span>
                )}
              </div>
              <AddEditDeleteBar1
                isVisible={true}
                showAdd={false}
                showEdit={canEdit}
                showDelete={false}
                showInfo={false}
                onEdit={() => {
                  setIsEditingPlans(true);
                  setPlanInputs(objective.plans || [{ text: '', url: '' }]);
                }}
              />
            </div>
          )}
        </div>
      </section>

      {/* Additional Information Section */}
      <div className="flex-grow flex flex-col min-h-0">
        <section
          tabIndex={0}
          className="flex flex-col h-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md"
          aria-labelledby="additional-info-heading"
        >
          <h2 id="additional-info-heading" className="text-lg font-semibold mb-2">
            Additional Information
          </h2>
          <div className="p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 relative flex-grow flex flex-col">
            {isEditingInfo ? (
              <div className="flex flex-grow">
                <textarea
                  value={infoText || objective.additionalInfo || ''}
                  onChange={(e) => setInfoText(e.target.value)}
                  onBlur={handleInfoSave}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setIsEditingInfo(false);
                  }}
                  className="w-full px-2 py-1 rounded border border-gray-300 dark:bg-zinc-700 dark:text-white flex-grow"
                  autoFocus
                  aria-label="Edit additional information"
                />
              </div>
            ) : (
              <div className="flex justify-between h-full">
                <div className="whitespace-pre-wrap flex-grow pr-12">
                  {objective.additionalInfo || 'No additional information'}
                </div>
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <AddEditDeleteBar1
                    isVisible={true}
                    showAdd={false}
                    showEdit={canEdit}
                    showDelete={false}
                    showInfo={false}
                    onEdit={() => {
                      setIsEditingInfo(true);
                      setInfoText(objective.additionalInfo || '');
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
